import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { LuChevronDown } from 'react-icons/lu';
import GlassIcon from '../ui/GlassIcon';
import { GLASS_CHIP } from '../ui/glass';
import { EASE_OUT } from '../ui/motionPresets';
import { getMenuIcon } from './menuConfig';
import { useTranslation } from '../../i18n/LanguageContext';

const ACTIVE_ICON_GRADIENT = 'tw-from-teal-400 tw-to-cyan-500 tw-shadow-teal-500/30';
const LEAVES_PATH = '/izin-yonetimi';

const rowClass = (isActive, compact) => [
  'tw-group tw-flex tw-w-full tw-min-w-0 tw-items-center tw-gap-3 tw-rounded-2xl tw-p-1.5 tw-text-left tw-text-sm tw-font-medium',
  'tw-transition-colors tw-duration-200 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50',
  compact ? 'tw-justify-center' : 'tw-pr-2.5',
  isActive
    ? 'tw-bg-teal-500/10 tw-text-teal-900 dark:tw-bg-teal-400/10 dark:tw-text-teal-100'
    : 'tw-text-slate-600 hover:tw-bg-slate-900/[0.04] hover:tw-text-slate-900 dark:tw-text-slate-300 dark:hover:tw-bg-white/[0.06] dark:hover:tw-text-white',
].join(' ');

const subLinkClass = (isActive) => [
  'tw-flex tw-min-w-0 tw-items-center tw-gap-2.5 tw-rounded-xl tw-px-3 tw-py-2 tw-text-[13px] tw-transition-colors tw-duration-200',
  'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50',
  isActive
    ? `tw-font-semibold tw-text-teal-800 dark:tw-text-teal-200 ${GLASS_CHIP}`
    : 'tw-text-slate-500 hover:tw-bg-slate-900/[0.04] hover:tw-text-slate-900 dark:tw-text-slate-400 dark:hover:tw-bg-white/[0.06] dark:hover:tw-text-white',
].join(' ');

/**
 * One top-level menu entry: a link, or a group that expands its submenu.
 * "Ana Sayfa" is both - the row opens the dashboard, the chevron the submenu.
 * In compact (collapsed) mode only the icon shows; the title becomes a tooltip.
 */
const SidebarItem = ({ item, pathname, isActive, expanded, compact, pendingLeaveCount, onToggle, onNavigate }) => {
  const { t, tMenu } = useTranslation();
  const Icon = getMenuIcon(item.path);
  const itemTitle = tMenu(item.path, item.title);
  const isGroup = item.submenu.length > 0;
  const opensPage = !isGroup || item.path === '/dashboard';
  const submenuId = `sidebar-submenu-${item.path.replace(/\W/g, '')}`;
  const hasPendingLeaves = pendingLeaveCount > 0 && item.submenu.some((sub) => sub.path === LEAVES_PATH);

  const icon = (
    <span className="tw-relative tw-shrink-0">
      {isActive ? (
        <GlassIcon gradient={ACTIVE_ICON_GRADIENT} size="xs">
          <Icon aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      ) : (
        <span className={`tw-grid tw-h-9 tw-w-9 tw-place-items-center tw-rounded-xl tw-text-slate-500 group-hover:tw-text-slate-800 dark:tw-text-slate-400 dark:group-hover:tw-text-white ${GLASS_CHIP}`}>
          <Icon aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </span>
      )}
      {hasPendingLeaves && (compact || !expanded) && (
        <span
          title={t('shell.pendingLeaves', { count: pendingLeaveCount })}
          className="tw-absolute -tw-right-0.5 -tw-top-0.5 tw-h-2.5 tw-w-2.5 tw-rounded-full tw-bg-rose-500 tw-ring-2 tw-ring-white dark:tw-ring-[#22262f]"
        />
      )}
    </span>
  );

  const chevron = (
    <LuChevronDown
      aria-hidden="true"
      className={`tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-400 tw-transition-transform tw-duration-200 ${expanded ? 'tw-rotate-180' : ''}`}
    />
  );

  const title = !compact && <span className="tw-min-w-0 tw-flex-1 tw-truncate">{itemTitle}</span>;

  return (
    <li className="tw-list-none">
      {opensPage ? (
        <div className="tw-flex tw-items-center tw-gap-1">
          <Link
            to={item.path}
            onClick={onNavigate}
            title={compact ? itemTitle : undefined}
            aria-current={pathname === item.path ? 'page' : undefined}
            className={rowClass(isActive, compact)}
          >
            {icon}
            {title}
          </Link>
          {isGroup && !compact && (
            <button
              type="button"
              onClick={() => onToggle(item.title)}
              aria-expanded={expanded}
              aria-controls={submenuId}
              aria-label={t('shell.submenuOf', { title: itemTitle })}
              className="tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-xl tw-bg-transparent hover:tw-bg-slate-900/[0.04] dark:hover:tw-bg-white/[0.06]"
            >
              {chevron}
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => onToggle(item.title)}
          title={compact ? itemTitle : undefined}
          aria-expanded={expanded}
          aria-controls={submenuId}
          className={rowClass(isActive, compact)}
        >
          {icon}
          {title}
          {!compact && chevron}
        </button>
      )}

      <AnimatePresence initial={false}>
        {isGroup && expanded && !compact && item.visibleSubmenu.length > 0 && (
          <motion.div
            key="submenu"
            id={submenuId}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: EASE_OUT }}
            className="tw-overflow-hidden"
          >
            <ul className="tw-ml-[1.6rem] tw-mt-1 tw-space-y-0.5 tw-border-l tw-border-slate-200 tw-pb-1 tw-pl-2.5 dark:tw-border-white/10">
              {item.visibleSubmenu.map((sub) => {
                const subActive = pathname === sub.path;
                const subTitle = tMenu(sub.path, sub.title);
                return (
                  <li key={sub.path} className="tw-list-none">
                    <Link
                      to={sub.path}
                      onClick={onNavigate}
                      title={subTitle}
                      aria-current={subActive ? 'page' : undefined}
                      className={subLinkClass(subActive)}
                    >
                      <span
                        aria-hidden="true"
                        className={`tw-h-1.5 tw-w-1.5 tw-shrink-0 tw-rounded-full ${subActive ? 'tw-bg-teal-500' : 'tw-bg-slate-300 dark:tw-bg-slate-600'}`}
                      />
                      <span className="tw-min-w-0 tw-flex-1 tw-truncate">{subTitle}</span>
                      {sub.path === LEAVES_PATH && pendingLeaveCount > 0 && (
                        <span
                          title={t('shell.pendingLeaves', { count: pendingLeaveCount })}
                          className="tw-grid tw-h-5 tw-min-w-[1.25rem] tw-place-items-center tw-rounded-full tw-bg-rose-500 tw-px-1.5 tw-text-[10px] tw-font-bold tw-text-white"
                        >
                          {pendingLeaveCount > 99 ? '99+' : pendingLeaveCount}
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
};

SidebarItem.propTypes = {
  item: PropTypes.shape({
    title: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
    submenu: PropTypes.array.isRequired,
    visibleSubmenu: PropTypes.array.isRequired,
  }).isRequired,
  pathname: PropTypes.string.isRequired,
  isActive: PropTypes.bool,
  expanded: PropTypes.bool,
  compact: PropTypes.bool,
  pendingLeaveCount: PropTypes.number,
  onToggle: PropTypes.func.isRequired,
  onNavigate: PropTypes.func,
};

export default SidebarItem;
